import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { PersistOptions } from "zustand/middleware";
import type { NodeDto, ScriptAllExamples, ScriptExample } from "~/types/api";
import type { CodeMirrorAutoCompletes } from "~/types/ui";
import {
  codeMirrorAutocompleteTypeEnum,
  ScriptExampleTypeEnum,
  scriptExampleTypeToString,
} from "~/utils/codeMirror-util";

export interface CodeMirrorState {
  codeExamples: ScriptAllExamples;
  baseAutocompletions: CodeMirrorAutoCompletes[];
  autocompletions: CodeMirrorAutoCompletes[];

  setCodeExamples: (codeExamples: ScriptExample[]) => void;
  setAutocompletions: (nodes: NodeDto[]) => void;
}

const useCodeMirrorState = create<CodeMirrorState>()(
  persist(
    (set, get) => ({
      codeExamples: {},
      baseAutocompletions: [],
      autocompletions: [],

      setCodeExamples: (examples: ScriptExample[]) => {
        for (var example of examples) {
          example.content ??= example.exampleInput
            ? example.exampleInput +
              "\n// Outputs: \n" +
              (example.exampleOutput ? example.exampleOutput : "")
            : "";
        }

        set(() => ({
          codeExamples: Object.groupBy(examples, ({ type }) => type) ?? {},
        }));

        const methods = examples
          .filter(
            (x) => x.type != ScriptExampleTypeEnum.CASE && x.name.includes("("),
          )
          .map((x) => {
            return {
              label: x.name.split("(").at(0)!,
              apply: x.name.split("(").at(0)! + "(",
              info:
                (x.name ? x.name : "") +
                (x.description && x.name ? "\n\n" : "") +
                (x.description ? x.description : ""),
              type: codeMirrorAutocompleteTypeEnum.METHOD,
            };
          });

        const variables = examples
          .map((x) => x.type)
          .filter(
            (value, index, self) =>
              self.indexOf(value) === index &&
              value != ScriptExampleTypeEnum.CASE,
          )
          .map((x) => {
            return {
              label: scriptExampleTypeToString(x),
              apply: scriptExampleTypeToString(x),
              info: "Sensor-Online Variable",
              type: codeMirrorAutocompleteTypeEnum.VARIABLE,
            };
          });

        set(() => ({ baseAutocompletions: methods.concat(variables) }));
      },

      setAutocompletions: (nodes) => {
        const base = get().baseAutocompletions;

        const deveuis = nodes.map((x) => {
          return {
            label: `${x.name}`,
            apply: `${x.deveui}`,
            info: "Node",
            type: codeMirrorAutocompleteTypeEnum.DEVEUI_LOOKUP,
          };
        });

        set(() => ({ autocompletions: base.concat(deveuis) }));
      },
    }),
    {
      name: "codeMirror-state",
    } as PersistOptions<CodeMirrorState>,
  ),
);

export default useCodeMirrorState;
